import Dexie from 'dexie';
import { config } from '../config';
import { describeUnknownError, StorageError } from '../app/errors';
import { log } from '../app/logger';
import { LATEST_PRODUCT_VERSION } from './migrations/product';

/**
 * **The dynamic-schema read path** — how a bundle reads a database whose schema it does not know.
 *
 * Design §13.5 promises *"a dedicated screen offering a **real BAK-1 export** … plus instructions to
 * update, rather than a crash"*, and `data/db.ts`'s `SchemaVersionError` message already tells the
 * user their matches *"can be exported from the recovery screen"*. Every other module in `data/`
 * reaches storage through `db()`, which **declares this bundle's schema** — so the export that
 * promise names could not be built, because the connection it would run on is the connection that
 * could not be opened. That is the gap build unit B-20 stopped on and B-21 was authorised to close.
 *
 * This module is the other connection: **a Dexie instance that declares nothing.**
 *
 * ── How it works, measured rather than assumed ───────────────────────────────
 *
 * A `Dexie` with no `.version()` call runs in what Dexie calls *dynamic* mode (`autoSchema`). Its
 * open issues `indexedDB.open(name)` with **no version**, which the IndexedDB specification answers
 * by opening the database at whatever version it already has — `upgradeneeded` never fires for an
 * existing database, so nothing is migrated, created or deleted. Dexie then reads the **stored**
 * schema back off the connection and populates `db.tables` from it. Verified in
 * `node_modules/dexie/dist/dexie.js` (the `state.autoSchema` branches of `tryOpenDB` and
 * `readGlobalSchema`) **and** driven in `recoveryDb.test.ts` against a database at a version this
 * bundle does not declare, holding a store it has never heard of.
 *
 * `Dexie.prototype.dynamicallyOpened()` reports the mode, so "this connection declared nothing" is a
 * postcondition a test can assert rather than a property of how the object happened to be built.
 *
 * ── Two properties this module is responsible for ────────────────────────────
 *
 * 1. **It never writes.** No `.version()`, therefore no upgrader; one `readonly` transaction; no
 *    delete, no clear, no put. §13.5's whole argument is that the naive response to a newer schema
 *    (delete and start over) is a silent data-loss path, so the module that exists to avoid it must
 *    not contain the ability. The one apparent exception is Dexie's own: opening an **absent**
 *    database in dynamic mode aborts the creation and deletes the empty shell it just made before
 *    rejecting `NoSuchDatabaseError`. That deletes a database that had no rows a microsecond ago and
 *    cannot reach one that does — but it is why `openRecoveryDb` is called only after a real open
 *    has already proved a database is there.
 *
 * 2. **It carries no bytes it does not have to.** `audioBlobs` rows hold the recordings themselves,
 *    and WebKit copies a Blob's bytes on read (§14 DD-2). A snapshot that kept them would hold a
 *    season of audio in memory to produce a JSON field. So rows are read one at a time through
 *    `.each()` — the same bound `repos/profile.ts` takes for the same reason — and **binary-valued
 *    properties are omitted from the snapshot and reported by name**. The bytes are fetched later,
 *    one at a time, through {@link readRecoveryValue}.
 *
 * ── Why it knows nothing about the archive ───────────────────────────────────
 *
 * There is no mention of an entity, a field list or a scope here. This module answers exactly one
 * question — *"what is in the database on this device?"* — and `app/recoveryExport.ts` decides what
 * an archive does with the answer. The split matters for the case the screen exists for: a store
 * this bundle has never heard of has to survive a layer that has no name for it, and a layer that
 * projected into `ArchiveRows` on the way out would have dropped it before anyone could decide.
 */

const MODULE = 'recovery';

/**
 * Dexie stores its version as `Math.round(version * 10)` in IndexedDB's native version field, so a
 * bundle declaring v4 opens native 40. Named because two places compare against it and a bare `10`
 * at either would read as arithmetic rather than as a Dexie encoding.
 */
export const DEXIE_NATIVE_VERSION_SCALE = 10;

/** This bundle's schema version, as IndexedDB stores it. */
export function bundleNativeVersion(): number {
  return LATEST_PRODUCT_VERSION * DEXIE_NATIVE_VERSION_SCALE;
}

/**
 * The schema version a native IndexedDB version number represents — **the number a user is shown.**
 *
 * `Math.floor`, not a plain divide, and both callers need the same rule. A stored native version is
 * not always a clean multiple of ten: when Dexie's schema-diff self-heal has run (it re-opens at
 * `native + 1` to add parts the running bundle declares and the stored database lacks) a v5 database
 * reads as native **51**, and `verno` reports **5.1**. That fraction is Dexie's bookkeeping, not a
 * fact about the user's data — putting it on a recovery screen would ask a coach at a pitch to
 * reason about a version number no release ever had.
 *
 * Declared here and used by `db.ts`'s guard as well, because two places rounding the same number
 * their own way is how the screen and the error start disagreeing about which version the data is.
 */
export function storedVersionOf(nativeVersion: number): number {
  return Math.floor(nativeVersion / DEXIE_NATIVE_VERSION_SCALE);
}

/**
 * Every row of every store, as the device holds them.
 *
 * Rows are `unknown` on purpose: this connection declared no schema, so there is no type to claim
 * them as, and a rescue that assumed a shape would drop whatever did not fit it.
 */
export interface RecoverySnapshot {
  /** The schema version the device's database is at — the number the user is shown. */
  readonly storedVersion: number;
  /** IndexedDB's own counter, unrounded. For the log; see {@link storedVersionOf}. */
  readonly storedNativeVersion: number;
  /** What this bundle declares. Lower than {@link storedVersion} is the whole condition. */
  readonly bundleVersion: number;
  /** Object-store names, sorted, exactly as the stored schema has them. */
  readonly storeNames: readonly string[];
  /** Rows per store, in the order the store yielded them, binary properties omitted. */
  readonly stores: Readonly<Record<string, readonly unknown[]>>;
  /** Rows per store. Derived from `stores` by construction — one object, never two counts. */
  readonly rowCounts: Readonly<Record<string, number>>;
  /**
   * `store.field` for every property omitted because it held bytes rather than data.
   *
   * Reported, never silent: on this device's schema it is exactly `audioBlobs.blob` and
   * `audioBlobs.chunks`, whose bytes travel as `blobs/` files in the archive. On a **newer** schema
   * it is how a binary field nobody here has heard of announces that it exists and that the JSON
   * side of this rescue is not carrying it.
   */
  readonly binaryFieldPaths: readonly string[];
  /**
   * Stores that could not be read at all, each with the reason.
   *
   * §13.5: *"an export must survive a damaged profile rather than refuse to run on one."* A rescue
   * that threw because one store was unreadable would lose the fourteen that were fine, on the one
   * screen whose whole purpose is not losing things. Empty in every healthy case — and it is
   * `serializeArchive`'s own posture (*"Nothing here throws on bad data"*) applied one layer down,
   * at the read rather than at the serializer.
   */
  readonly unreadableStores: readonly string[];
}

/**
 * What the screen needs to describe the situation, **without reading a single row.**
 *
 * The recovery screen's first job is to tell a user which version their data is and which version
 * the app is. Deriving that from {@link readRecoverySnapshot} would read every row of every store to
 * paint a heading — a season of observations, at a venue, before the screen says anything. These are
 * `count()`s, which IndexedDB answers from the store rather than by materialising rows.
 */
export interface RecoveryOverview {
  readonly storedVersion: number;
  readonly storedNativeVersion: number;
  readonly bundleVersion: number;
  readonly storeNames: readonly string[];
  readonly rowCounts: Readonly<Record<string, number>>;
  /** Every row in the database, across every store. The one number that reassures. */
  readonly totalRows: number;
}

let connection: Dexie | null = null;

/**
 * The dynamic connection, opened at the database's own version.
 *
 * @param name defaults to the product database. A parameter so a test can point it at an isolated
 *   database, the same seam `data/db.ts` offers for the same reason.
 * @throws {StorageError} when there is no such database, or the open failed for any other reason.
 *   Never `SchemaVersionError`: a connection that declares no version cannot be out of date.
 */
export async function openRecoveryDb(name: string = config.PRODUCT_DB_NAME): Promise<Dexie> {
  // The name is part of the cache key, not just of the miss. Returning a held connection for a
  // *different* name would hand a caller the wrong database and every row it then read would be
  // real, consistent and about something else.
  if (connection !== null && connection.isOpen() && connection.name === name) return connection;
  if (connection !== null) closeRecoveryDb();

  // No `.version()` call anywhere in this function, and that omission IS the mechanism.
  const handle = new Dexie(name);
  try {
    await handle.open();
  } catch (cause) {
    const missing = cause instanceof Error && cause.name === 'NoSuchDatabaseError';
    throw new StorageError(
      missing
        ? 'There is no Touchline database on this device to recover.'
        : 'The Touchline database on this device could not be opened for recovery.',
      { cause, context: { module: MODULE, name, missing } },
    );
  }

  connection = handle;
  return handle;
}

/** Closes and forgets the recovery connection. Not a delete: a closed connection loses nothing. */
export function closeRecoveryDb(): void {
  connection?.close();
  connection = null;
}

/**
 * True when the value is bytes rather than data.
 *
 * `File` is a `Blob`, and `DataView`/every typed array is an `ArrayBufferView`, so the three checks
 * cover every binary value IndexedDB's structured clone can hold. A rescue must **notice** one:
 * `JSON.stringify(new ArrayBuffer(8))` is `{}`, which would put an empty object in a rescue file and
 * say nothing about the bytes that used to be there.
 */
function isBinary(value: unknown): boolean {
  if (typeof Blob !== 'undefined' && value instanceof Blob) return true;
  if (value instanceof ArrayBuffer) return true;
  return ArrayBuffer.isView(value);
}

/** A `Blob[]` — `AudioBlobRow.chunks` before finalize, and the general array-of-bytes case. */
function isBinaryArray(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0 && value.every(isBinary);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * One row, with its binary properties removed and their paths recorded.
 *
 * Returns the row **unchanged** — the same object reference — when it carries none, which is every
 * row of every store but `audioBlobs`. That keeps the common path allocation-free and makes the
 * copy visibly the exceptional case rather than the default.
 */
function withoutBytes(store: string, row: unknown, binaryPaths: Set<string>): unknown {
  if (!isRecord(row)) return row;

  let stripped: Record<string, unknown> | null = null;
  for (const [key, value] of Object.entries(row)) {
    if (!isBinary(value) && !isBinaryArray(value)) continue;
    stripped ??= { ...row };
    delete stripped[key];
    binaryPaths.add(`${store}.${key}`);
  }
  return stripped ?? row;
}

/**
 * Reads every row of every store — **one transaction if it can, store by store if it must.**
 *
 * The single transaction is `repos/profile.ts`'s reason: an archive assembled from independently
 * timed reads describes a moment that never existed. It matters less here (a device showing this
 * screen has an app that cannot write to this database at all — `data/db.ts`'s guard refuses every
 * transaction against a newer schema) but it costs nothing, so it is what this tries first.
 *
 * **The fallback is §13.5, and it is the reason this function does not simply throw.** *"An export
 * must survive a damaged profile rather than refuse to run on one."* An IndexedDB transaction that
 * hits an error is aborted as a whole, so one unreadable store inside the single transaction takes
 * the other fourteen with it — the rescue would lose everything that was fine because something was
 * not. So a failure re-reads store by store, in separate transactions, and records the ones that
 * genuinely cannot be read in {@link RecoverySnapshot.unreadableStores} rather than raising.
 *
 * @throws {StorageError} only when the database cannot be opened, or when **every** store failed —
 *   at which point there is nothing to rescue and saying so is the honest answer.
 */
export async function readRecoverySnapshot(
  name: string = config.PRODUCT_DB_NAME,
): Promise<RecoverySnapshot> {
  const handle = await openRecoveryDb(name);
  const storeNames = handle.tables.map((table) => table.name).sort();
  const binaryPaths = new Set<string>();
  const stores: Record<string, unknown[]> = {};
  const rowCounts: Record<string, number> = {};
  const unreadableStores: string[] = [];

  /** One store's rows, projected. `.each()`, never `.toArray()`: peak memory is one row (§11.2). */
  const collect = async (store: string): Promise<unknown[]> => {
    const rows: unknown[] = [];
    await handle.table(store).each((row: unknown) => {
      rows.push(withoutBytes(store, row, binaryPaths));
    });
    return rows;
  };

  const keep = (store: string, rows: unknown[]): void => {
    stores[store] = rows;
    rowCounts[store] = rows.length;
  };

  try {
    await handle.transaction('r', handle.tables, async () => {
      for (const store of storeNames) keep(store, await collect(store));
    });
  } catch {
    // The whole-snapshot read failed, which under IndexedDB means it was aborted as a whole — so
    // nothing gathered above can be trusted and every store is re-read on its own.
    for (const key of Object.keys(stores)) delete stores[key];
    for (const key of Object.keys(rowCounts)) delete rowCounts[key];
    binaryPaths.clear();

    for (const store of storeNames) {
      try {
        keep(store, await handle.transaction('r', [handle.table(store)], () => collect(store)));
      } catch (cause) {
        unreadableStores.push(store);
        // Reported here as well as on the snapshot: NFR-8 forbids a silent loss, and by the time
        // this reaches a screen it is one entry in a list rather than a diagnosable failure.
        log.error(MODULE, '§13.5: a store could not be read and is missing from the rescue', {
          store,
          name,
          error: describeUnknownError(cause),
        });
      }
    }

    if (unreadableStores.length === storeNames.length && storeNames.length > 0) {
      throw new StorageError('Nothing on this device could be read for recovery.', {
        context: { module: MODULE, name, unreadableStores },
      });
    }
  }

  return {
    storedVersion: storedVersionOf(handle.backendDB().version),
    storedNativeVersion: handle.backendDB().version,
    bundleVersion: LATEST_PRODUCT_VERSION,
    storeNames,
    stores,
    rowCounts,
    binaryFieldPaths: [...binaryPaths].sort(),
    unreadableStores,
  };
}

/**
 * Counts, not rows — see {@link RecoveryOverview}.
 *
 * @throws {StorageError} when the database cannot be opened or a store cannot be counted.
 */
export async function readRecoveryOverview(
  name: string = config.PRODUCT_DB_NAME,
): Promise<RecoveryOverview> {
  const handle = await openRecoveryDb(name);
  const storeNames = handle.tables.map((table) => table.name).sort();
  const rowCounts: Record<string, number> = {};

  let counting = '';
  try {
    await handle.transaction('r', handle.tables, async () => {
      for (const store of storeNames) {
        counting = store;
        rowCounts[store] = await handle.table(store).count();
      }
    });
  } catch (cause) {
    throw new StorageError(`Could not count "${counting}" in the database being recovered.`, {
      cause,
      context: { module: MODULE, store: counting, name },
    });
  }

  return {
    storedVersion: storedVersionOf(handle.backendDB().version),
    storedNativeVersion: handle.backendDB().version,
    bundleVersion: LATEST_PRODUCT_VERSION,
    storeNames,
    rowCounts,
    totalRows: storeNames.reduce((sum, store) => sum + (rowCounts[store] ?? 0), 0),
  };
}

/**
 * Every row of **one** store, off the dynamic connection. Binary properties omitted, as everywhere.
 *
 * Added by build unit B-22 for the one store §13.5's rescue needs that is not in the database being
 * recovered: the OBS-1 ring lives in the harness database (`app/logger.ts`'s `LOG_RING_STORE`), so
 * carrying it means reading a *second* database — and it must be read the same schema-less way as
 * the first. Declaring this bundle's harness schema against it would be the exact hazard §13.5
 * exists to avoid, twice over: it can **create** an absent database (an upgrade transaction is a
 * write, and this screen never writes), and against a database a *newer* bundle wrote it opens with
 * this bundle's version and then truncates unseen fields on any later write (the Dexie behaviour
 * B-21 measured). A connection that declares nothing does neither.
 *
 * `readRecoverySnapshot` is deliberately not reused for this: it reads every store of the database,
 * which for the harness database means the measurement runs and their audio.
 *
 * @returns the store's rows, or `[]` when the database has no such store — the same posture as
 *   {@link readRecoveryValue}, because a rescue asks about stores a device may not have.
 * @throws {StorageError} when the database cannot be opened, or the store cannot be read.
 */
export async function readRecoveryStore(store: string, name: string): Promise<unknown[]> {
  const handle = await openRecoveryDb(name);
  if (!handle.tables.some((table) => table.name === store)) return [];
  const binaryPaths = new Set<string>();
  const rows: unknown[] = [];
  try {
    await handle.table(store).each((row: unknown) => {
      rows.push(withoutBytes(store, row, binaryPaths));
    });
  } catch (cause) {
    throw new StorageError(`Could not read ${store} out of "${name}" for recovery.`, {
      cause,
      context: { module: MODULE, store, name },
    });
  }
  return rows;
}

/**
 * One row by primary key, **with** whatever bytes it holds.
 *
 * The counterpart to {@link readRecoverySnapshot}'s omission: the snapshot is the index, this is how
 * a caller fetches the one row it wants the bytes of, one at a time. `app/recoveryExport.ts` builds
 * the archive's `ArchiveBlobReader` on it.
 *
 * @returns the row, or `undefined` when the store or the key is absent — a rescue asks about rows a
 *   damaged profile may not have, and that is a report, not a failure.
 * @throws {StorageError} on a read failure.
 */
export async function readRecoveryValue(
  store: string,
  key: string,
  name: string = config.PRODUCT_DB_NAME,
): Promise<unknown> {
  const handle = await openRecoveryDb(name);
  if (!handle.tables.some((table) => table.name === store)) return undefined;
  try {
    return await handle.table(store).get(key);
  } catch (cause) {
    throw new StorageError(`Could not read ${store}/${key} out of the database being recovered.`, {
      cause,
      context: { module: MODULE, store, key, name },
    });
  }
}

/** Test seam only — lets a test observe or replace the process-wide recovery connection. */
export function __setRecoveryDbForTests(next: Dexie | null): void {
  connection = next;
}
